import React, { Component } from "react";
import moment from "moment";
import API, { URL_PATH } from "../../services/API";
import redux from "../../common/Redux";
import { 
  Container, 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Paper,
  List,
  ListItem,
  IconButton
} from "@mui/material";
import SendIcon from '@mui/icons-material/Send';
import Toast from "../../components/Toast";
import Header from "../../components/Header";
import { initSocket } from "../../services/socket";

class CreateScreen extends Component {
  constructor(props) {
    super(props);
    this.state = {
      id: this.props.match.params.id,
      type: "",
      subject: "",
      message: "",
      comments: [],
      created_at: "",
      ip_address: "",
      comment: "",
      toast: {
        open: false,
        message: "",
        severity: "success"
      },
      socketConnected: false
    };
    
    this.socket = null;
  }

  componentDidMount() {
    console.log("Component mounted, initializing data and socket connection");
    this.fetchData(this.props.match.params.id);
    this.initializeSocketConnection();
  }

  componentWillUnmount() {
    console.log("Component unmounting, cleaning up socket connection");
    if (this.socket) {
      this.socket.off(`etable_database_support-ticket.${this.state.id}:support-ticket-event`);
      this.socket.off('connect');
      this.socket.off('disconnect');
      this.socket.disconnect();
    }
  }

  initializeSocketConnection = () => {
    try {
      console.log("Initializing socket connection...");
      const hotelId = this.props.selectedHotelId?.id;
      if (!hotelId) {
        console.warn("No hotel ID available for socket connection");
        return;
      }

      this.socket = initSocket(hotelId);
      
      // Connection status handlers
      this.socket.on('connect', () => {
        console.log("✅ Socket.IO connected successfully");
        this.setState({ socketConnected: true });
        this.subscribeToTicketEvents();
      });

      this.socket.on('disconnect', (reason) => {
        console.log("❌ Socket.IO disconnected:", reason);
        this.setState({ socketConnected: false });
        // this.showToast("Disconnected from real-time updates", "warning");
      });

      this.socket.on('connect_error', (error) => {
        console.error("Socket connection error:", error);
      });

    } catch (error) {
      console.error("Error initializing socket connection:", error);
      this.showToast("Failed to initialize real-time connection", "error");
    }
  };

  subscribeToTicketEvents = () => {
    if (!this.socket) return;

    const ticketId = this.state.id;
    console.log(`Subscribing to ticket events for ticket ID: ${ticketId}`);
    
    // Remove previous listeners to avoid duplicates
    this.socket.off(`etable_database_support-ticket.${ticketId}:support-ticket-event`);
    
    // Listen for support ticket events with the exact pattern from server
    this.socket.on(`etable_database_support-ticket.${ticketId}:support-ticket-event`, (data) => {
      console.log("📢 Received support ticket event:", data);
      
      // The event data structure matches what we see in server logs
      if (data.data?.ticket_id == this.state.id) {
        this.handleTicketEvent(data.data); // Note we're using data.data
      } else {
        console.log(`Event for different ticket (ID: ${data.data?.ticket_id}), ignoring`);
      }
    });
  };

  handleTicketEvent = (eventData) => {
    try {
      console.log("Processing ticket event:", eventData);
      const { type, data: newComment } = eventData;
      
      if (type === 'replied') {
        this.setState(prevState => ({
          comments: [...prevState.comments, newComment]
        }), () => {
          console.log("Updated comments with new reply");
        });
        
        this.showToast('New reply received', 'info');
      }
    } catch (error) {
      console.error("Error processing ticket event:", error);
      this.showToast("Error processing update", "error");
    }
  };

  showToast = (message, severity = "success") => {
    this.setState({
      toast: {
        open: true,
        message,
        severity
      }
    });
  };

  handleCloseToast = (event, reason) => {
    if (reason === 'clickaway') return;
    this.setState({ toast: { ...this.state.toast, open: false } });
  };

  fetchData = async (id) => {
    console.log(`Fetching ticket data for ID: ${id}`);
    try {
      const { status, message, data } = await API(
        this,
        "get",
        URL_PATH.getTicketDetails(id)
      );
      
      if (status == "success") {
        console.log("Successfully fetched ticket data");
        const { id, type, title, description, comments, created_at, ip_address } = data;
        this.setState({
          id,
          type,
          subject: title,
          message: description,
          comments,
          created_at,
          ip_address,
        });
      } else {
        console.warn("Failed to fetch ticket data:", message);
        this.showToast(message, "error");
      }
    } catch (error) {
      console.error("Error fetching ticket data:", error);
      this.showToast("Failed to load ticket data", "error");
    }
  };

  saveData = async () => {
    if (!this.state.comment.trim()) {
      console.log("Empty comment, not saving");
      return;
    }
    
    console.log("Saving new comment");
    
    try {
      const formData = new FormData();
      formData.append("ticket_id", this.state.id);
      formData.append("comment", this.state.comment);
      
      const { status, message, data } = await API(
        this,
        "post",
        URL_PATH.addCommentToTicket,
        formData
      );
      if (status == "success") {
        console.log("Comment saved successfully");
        this.showToast(message, "success");

        this.setState({
          comment: "",
          comments: [...this.state.comments, data],
        });
      } else {
        console.warn("Failed to save comment:", message);
        this.showToast(message, "error");
      }
    } catch (error) {
      console.error("Error saving comment:", error);
      this.showToast("Failed to save comment", "error");
    }
  };

  handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this.saveData();
    }
  };

  render() {
    return (
      <Container maxWidth="md" sx={{ py: 3, borderWidth: "1px", borderColor: "#FFC857", borderStyle: "solid", borderRadius: "8px" }}>
        {/* Connection Status Indicator */}
        <Box sx={{ position: 'absolute', top: 10, right: 10 }}>
          <Box sx={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            bgcolor: this.state.socketConnected ? 'success.main' : 'error.main',
            display: 'inline-block',
            mr: 1
          }} />
          <Typography variant="caption" color="text.secondary">
            {this.state.socketConnected ? 'Connected' : 'Disconnected'}
          </Typography>
        </Box>

        {/* Ticket Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Ticket #{this.state.id}
          </Typography>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            {this.state.subject}
          </Typography>
          
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            color: 'text.secondary',
            fontSize: '0.875rem',
            mb: 2
          }}>
            <span>Type: {this.state.type}</span>
            <span>Created: {moment(this.state.created_at).format("MMM D, YYYY h:mm A")}</span>
            <span>IP: {this.state.ip_address}</span>
          </Box>
        </Box>

        {/* Chat Container */}
        <Paper elevation={0} sx={{ borderRadius: 1, height: '60vh', display: 'flex', flexDirection: 'column' }}>
          {/* Messages Area */}
          <Box sx={{ flex: 1, overflowY: 'auto', p: 2, bgcolor: '#041A29' }}>
            <List sx={{ width: '100%' }}>
              {this.state.comments.map((item, index) => (
                <ListItem 
                  key={index}
                  sx={{
                    display: 'flex',
                    justifyContent: item.user_id === "0" ? 'flex-start' : 'flex-end',
                    px: 1,
                    py: 0.5
                  }}
                >
                  <Box sx={{
                    maxWidth: '80%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: item.user_id === "0" ? 'flex-start' : 'flex-end'
                  }}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.5,
                        border: "1px solid #FFC857",
                        borderRadius: item.user_id === "0" ? '16px 16px 16px 4px' : '16px 16px 4px 16px',
                        bgcolor: item.user_id === "0" ? '#FFC857' : '#041A29',
                        color: item.user_id === "0" ? 'primary.contrastText' : 'white'
                      }}
                    >
                      <Typography variant="body2">
                        {item.comment}
                      </Typography>
                    </Paper>
                    <Typography variant="caption" sx={{ mt: 0.5, color: 'text.secondary' }}>
                      {moment(item.created_at).format("h:mm A")}
                    </Typography>
                  </Box>
                </ListItem>
              ))}
            </List>
          </Box>

          {/* Input Area */}
          <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: '#041A29' }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                multiline
                maxRows={4}
                variant="outlined"
                size="small"
                placeholder="Type your reply..."
                value={this.state.comment}
                onChange={(e) => this.setState({ comment: e.target.value })}
                onKeyPress={this.handleKeyPress}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '20px',
                  }
                }}
              />
              <IconButton
                color="primary"
                onClick={this.saveData}
                disabled={!this.state.comment.trim()}
                sx={{ alignSelf: 'flex-end', mb: 0.5 }}
              >
                <SendIcon />
              </IconButton>
            </Box>
          </Box>
        </Paper>

        <Toast
          open={this.state.toast.open}
          message={this.state.toast.message}
          severity={this.state.toast.severity}
          onClose={this.handleCloseToast}
        />
      </Container>
    );
  }
}

export default redux(CreateScreen);